import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApi } from '../../api';
import {
  FaUser, FaCarSide, FaExclamationCircle, FaClipboardCheck,
  FaWrench, FaSignature, FaArrowLeft, FaFilePdf, FaClipboardList,
} from 'react-icons/fa';
import { formatearPrecio, abrirPdfAutenticado } from '../utils';
import {
  Alert, Box, Button, Checkbox, Chip, FormControlLabel, Grid,
  Slider, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TextField, Typography, Container,
} from '@mui/material';

const ESTADO_INFO = {
  pendiente: { label: 'Pendiente', color: 'warning' },
  en_proceso: { label: 'En Proceso', color: 'info' },
  completado: { label: 'Completado', color: 'success' },
  cerrada: { label: 'Cerrada', color: 'default' },
};

const OrdenDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const api = useApi();
  const [orden, setOrden] = useState(null);
  const [vehiculo, setVehiculo] = useState(null);
  const [reparaciones, setReparaciones] = useState([]);
  const [cargando, setCargando] = useState(true);

  useEffect(() => {
    const fetchDatos = async () => {
      setCargando(true);
      try {
        const [resOrden, resReparaciones] = await Promise.all([
          api.get(`${process.env.REACT_APP_API_URL}/api/ordenes/${id}/`),
          api.get(`${process.env.REACT_APP_API_URL}/api/reparaciones/?orden=${id}`),
        ]);
        setOrden(resOrden.data);
        setReparaciones(resReparaciones.data);

        if (resOrden.data.vehiculo) {
          try {
            const resVehiculo = await api.get(
              `${process.env.REACT_APP_API_URL}/api/vehiculos/${resOrden.data.vehiculo}/`
            );
            setVehiculo(resVehiculo.data);
          } catch (err) {
            console.error('Error cargando datos del vehículo:', err);
          }
        }
      } catch (error) {
        console.error('Error cargando orden:', error);
      } finally {
        setCargando(false);
      }
    };
    fetchDatos();
  }, [id]);

  if (cargando || !orden) {
    return <Typography sx={{ textAlign: 'center', mt: 5 }}>Cargando...</Typography>;
  }

  const checklist = Object.keys(orden.recepcion || {});
  const totalReparaciones = reparaciones.reduce((sum, r) => sum + parseFloat(r.costo || 0), 0);
  const estadoInfo = ESTADO_INFO[orden.estado] || { label: orden.estado, color: 'default' };

  return (
    <Container maxWidth="lg" sx={{ my: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" component="h3" className="titulo-app" sx={{ mb: 0 }}>
          Orden de Servicio #{orden.id}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip label={estadoInfo.label} color={estadoInfo.color} size="small" />
          {orden.pdf_url && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<FaFilePdf />}
              onClick={() => abrirPdfAutenticado(orden.pdf_url, api)}
            >
              Ver PDF
            </Button>
          )}
          <Button size="small" variant="outlined" color="inherit" startIcon={<FaArrowLeft />} onClick={() => navigate('/ordenes')}>
            Volver
          </Button>
        </Box>
      </Box>

      <Alert severity="info" sx={{ mb: 4 }}>
        Vista de solo lectura — así quedó registrada la orden.
      </Alert>

      <div className="card card-app mb-4">
        <div className="card-header-app"><FaClipboardList /> DATOS DE LA ORDEN</div>
        <div className="card-body">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField label="Fecha de ingreso" value={orden.fecha_recepcion || ''} disabled fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Fecha de entrega" value={orden.fecha_entrega || ''} disabled fullWidth />
            </Grid>
          </Grid>
        </div>
      </div>

      <div className="card card-app mb-4">
        <div className="card-header-app"><FaUser /> DATOS DEL CLIENTE</div>
        <div className="card-body">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField label="Cliente" value={orden.cliente || ''} disabled fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Dirección" value={orden.direccion || ''} disabled fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Teléfono" value={orden.telefono || ''} disabled fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField label="Email" value={orden.email || ''} disabled fullWidth />
            </Grid>
          </Grid>
        </div>
      </div>

      <div className="card card-app mb-4">
        <div className="card-header-app"><FaCarSide /> DATOS DEL VEHÍCULO</div>
        <div className="card-body">
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <TextField label="Placa" value={orden.vehiculo || ''} disabled fullWidth />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField label="Marca" value={vehiculo?.marca || ''} disabled fullWidth />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField label="Modelo" value={vehiculo?.modelo || ''} disabled fullWidth />
            </Grid>
            <Grid item xs={12} md={2}>
              <TextField label="Color" value={vehiculo?.color || ''} disabled fullWidth />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField label="Línea" value={vehiculo?.linea || ''} disabled fullWidth />
            </Grid>
          </Grid>
        </div>
      </div>

      <div className="card card-app mb-4">
        <div className="card-header-app"><FaExclamationCircle /> SERVICIO REQUERIDO</div>
        <div className="card-body">
          <TextField
            label="Servicio Requerido"
            value={orden.descripcion || ''}
            disabled
            multiline
            rows={4}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Observaciones"
            value={orden.observaciones || ''}
            disabled
            multiline
            rows={3}
            fullWidth
          />
        </div>
      </div>

      <div className="card card-app mb-4">
        <div className="card-header-app"><FaClipboardCheck /> RECEPCIÓN DEL VEHÍCULO</div>
        <div className="card-body">
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              {checklist.length === 0 ? (
                <Typography color="text.secondary">No hay checklist de recepción registrado.</Typography>
              ) : (
                <Grid container>
                  {checklist.map((item) => (
                    <Grid item xs={12} sm={6} key={item}>
                      <FormControlLabel
                        control={<Checkbox checked={!!orden.recepcion[item]} disabled />}
                        label={item}
                      />
                    </Grid>
                  ))}
                </Grid>
              )}
            </Grid>
            <Grid item xs={12} md={6} sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#083a94', mb: 1 }}>
                Esquema del vehículo
              </Typography>
              {orden.esquema ? (
                <img
                  src={orden.esquema}
                  alt="Esquema del vehículo"
                  style={{ maxWidth: 280, width: '100%', border: '1px solid #ccc' }}
                />
              ) : (
                <Typography color="text.secondary">No hay esquema registrado</Typography>
              )}
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mt: 3 }}>
            <Grid item xs={12} md={6}>
              <TextField label="Kilometraje" value={orden.kilometraje || ''} disabled fullWidth />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#083a94' }}>
                Gasolina
              </Typography>
              <Slider min={0} max={100} value={Number(orden.gasolina || 0)} disabled />
              <Typography variant="body2" sx={{ textAlign: 'right' }}>{orden.gasolina || 0}%</Typography>
            </Grid>
          </Grid>
        </div>
      </div>

      <div className="card card-app mb-4">
        <div className="card-header-app">ABONO RECIBIDO</div>
        <div className="card-body">
          <TextField
            label="Valor abonado por el cliente ($)"
            value={`$${formatearPrecio(orden.abono || 0)}`}
            disabled
            fullWidth
          />
        </div>
      </div>

      <div className="card card-app mb-4">
        <div className="card-header-app"><FaWrench /> REPARACIONES REALIZADAS</div>
        <div className="card-body" style={{ padding: 0 }}>
          {reparaciones.length === 0 ? (
            <Typography color="text.secondary" sx={{ p: 3, mb: 0 }}>No hay reparaciones registradas.</Typography>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.900' }}>
                    <TableCell sx={{ color: '#fff' }}>#</TableCell>
                    <TableCell sx={{ color: '#fff' }}>Descripción</TableCell>
                    <TableCell sx={{ color: '#fff' }}>Técnico</TableCell>
                    <TableCell sx={{ color: '#fff' }}>Fecha</TableCell>
                    <TableCell sx={{ color: '#fff' }} align="right">Costo</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reparaciones.map((r, i) => (
                    <TableRow key={r.id}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{r.garantia ? `[Garantía] ${r.descripcion}` : r.descripcion}</TableCell>
                      <TableCell>{r.tecnico?.nombre || '—'}</TableCell>
                      <TableCell>{r.fecha}</TableCell>
                      <TableCell align="right">${formatearPrecio(r.costo)}</TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell colSpan={4} align="right" sx={{ fontWeight: 'bold' }}>TOTAL</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>${formatearPrecio(totalReparaciones)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
      </div>

      <div className="card card-app mb-4">
        <div className="card-header-app"><FaSignature /> AUTORIZACIÓN Y FIRMA</div>
        <div className="card-body">
          <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column' }}>
            <FormControlLabel
              control={<Checkbox checked={!!orden.presupuesto} disabled />}
              label="Solicitó presupuesto previo antes de autorizar el trabajo"
            />
            <FormControlLabel
              control={<Checkbox checked={!!orden.autoriza_reparacion} disabled />}
              label="Autorizó realizar reparación sin presupuesto previo"
            />
            <FormControlLabel
              control={<Checkbox checked={!!orden.autoriza_prueba} disabled />}
              label="Autorizó conducir el vehículo para pruebas"
            />
          </Box>

          <Typography variant="body2" sx={{ fontWeight: 600, color: '#083a94', mb: 1 }}>
            Firma del cliente
          </Typography>
          {orden.firma ? (
            <img src={orden.firma} alt="Firma del cliente" style={{ maxWidth: 300, border: '1px solid #ccc' }} />
          ) : (
            <Typography color="text.secondary">No hay firma registrada</Typography>
          )}
        </div>
      </div>

      <Box sx={{ textAlign: 'center', my: 4 }}>
        <Button variant="outlined" color="inherit" startIcon={<FaArrowLeft />} onClick={() => navigate('/ordenes')}>
          Volver a Órdenes
        </Button>
      </Box>
    </Container>
  );
};

export default OrdenDetalle;
